import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {RootState} from '@src/redux/store';
import {BASE_URL} from '@src/helpers/constants';
import {retrieveItem} from '@src/helpers/functions';
import * as TYPES from '@src/redux/types';

const GRAPHQL_QUERY = `
  query getExportRequestList(
    $filter: exportRequestFilterInput
    $pagination: paginationInput
    $sort: [sortInput!]
  ) {
    getExportRequestList(filter: $filter, pagination: $pagination, sort: $sort) {
      items {
        id
        userId
        status
        requestedDatetime
        completedDatetime
        url
        urlExpiresAt
        fileName
        rowCount
        errorMessage
      }
      pagination {
        pageNumber
        pageLimit
        totalCount
      }
      error {
        message
        statusCode
      }
    }
  }
`;

export type GetExportRequestListParams = {
  filter?: TYPES.ExportRequestFilterInput;
  pagination?: TYPES.PaginationInput;
  sort?: TYPES.SortInput[];
};

const initialState: TYPES.ExportRequestListResponseState = {
  result: null,
  error: null,
  loading: false,
};

export const getExportRequestListRequest = createAsyncThunk<
  TYPES.ExportRequestListResponse,
  GetExportRequestListParams,
  {extra: ApolloClient<NormalizedCacheObject>}
>('export_request/get_list', async (params: GetExportRequestListParams) => {
  const sessionId = retrieveItem('sessionId') ?? '';
  try {
    const response = await fetch(BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        sessionId: sessionId,
      },
      body: JSON.stringify({
        operationName: 'getExportRequestList',
        query: GRAPHQL_QUERY,
        variables: params ?? {},
      }),
    });
    const json = await response.json();
    if (json?.errors?.length) {
      const first = json.errors[0];
      return Promise.reject({
        message: first.message || 'Failed to load export requests',
        statusCode: String(response.status),
      });
    }
    return json?.data?.getExportRequestList as TYPES.ExportRequestListResponse;
  } catch (error) {
    const err = error as {message?: string; statusCode?: string};
    return Promise.reject({
      message: err.message || 'An error occurred',
      statusCode: err.statusCode || '500',
    });
  }
});

const getExportRequestListSlice = createSlice({
  name: 'get_export_request_list',
  initialState: initialState,
  reducers: {
    getExportRequestListClear: state => {
      state.result = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getExportRequestListRequest.pending, state => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getExportRequestListRequest.fulfilled, (state, action) => {
        state.result = action.payload;
        state.loading = false;
      })
      .addCase(getExportRequestListRequest.rejected, (state, action) => {
        state.error =
          (action.payload as TYPES.Error) ||
          {message: 'Failed to fetch', statusCode: '500'};
        state.loading = false;
      });
  },
});

export const {getExportRequestListClear} = getExportRequestListSlice.actions;

export const getExportRequestList = createSelector(
  (state: RootState) => state.exportRequest.getExportRequestList.result,
  (state: RootState) => state.exportRequest.getExportRequestList.error,
  (state: RootState) => state.exportRequest.getExportRequestList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading,
  }),
);

export default getExportRequestListSlice.reducer;
